import React, { useState, useEffect, useMemo } from "react";
import { AddEditCommitted } from "./lazyForms";
import CommonDataView from "./CommonDataView";
import SheetFooter from "./components/SheetFooter";
import useTabularDataManager from "./useTabularDataManager";
import useFieldOrder from "./useFieldOrder";
import FloatingDownloadButton from "./FloatingDownloadButton";

function Cash({ cars, setCars, pagination, setPagination, initialSelectedRow = "" , isLoading = false }){
    const ownSheetName = "Cash";

    const {
        modalVisible, setModalVisibility,
        messagesModalVisible, setMessagesModalVisible,
        sortOrder, sortData,
        filterObject, filterData,
        filteredDataArray
        } = useTabularDataManager(cars, setCars);

    //Pagination changes refetch automatically: the useSheetData query key in
    //SubNav includes currentPage/recordsPerPage/searchTerm/dateFrom/dateTo

    return (
        <div className="flex flex-col h-full sheet-viewport" id="cash-content">
            <Content isLoading={isLoading}
                modalVisible={modalVisible}
                setModalVisibility={setModalVisibility}
                filteredDataArray={filteredDataArray} setCars={setCars} sortData={sortData}
                sortOrder={sortOrder} parentSheetName={ownSheetName}
                filterData={filterData} filterObject={filterObject}
                ownSheetName={ownSheetName}
                initialSelectedRow={initialSelectedRow}
                pagination={pagination}
                setPagination={setPagination}
            />
            <Summary
              pagination={pagination}
              setPagination={setPagination}
            />
        </div>
    )
}

function Content({ filteredDataArray, setCars, modalVisible, setModalVisibility,
  sortData, sortOrder, parentSheetName, filterData, filterObject, ownSheetName, initialSelectedRow = "",
  pagination, setPagination, isLoading }){
    const [fieldOrder, setFieldOrder] = useFieldOrder(parentSheetName, filteredDataArray[0], ['Make', 'Model', 'ModelYear', 'CustomerName', 'AgreedPrice']);
    const [ currentVehicleIndex, setCurrentVehicle ] = useState();
    const openModalEdit = (vehicleId)=>{
        setCurrentVehicle(vehicleId);
        setModalVisibility({
            visible: true,
            mode: "edit"
        });
    }

    const [ selectedRow, setSelectedRow ] = useState(initialSelectedRow)

    useEffect(() => {
        if (initialSelectedRow) setSelectedRow(initialSelectedRow);
    }, [filteredDataArray, initialSelectedRow]);

    const selectRow = (rowIndex)=>{
        setSelectedRow(rowIndex)
    }

    // Closures reference no component state — stable for the component lifetime.
    const formattingRules = useMemo(() => ({
        Days: {
            formatByValue: (val)=>{
                if(val > 240 && val <= 270){
                    return {color: "orange", fontWeight: "bold"}
                }
                if(val > 270){
                    return {color: "red", fontWeight: "bold"}
                }
                return {color: "green", fontWeight: "bold"}
            }
        },
        DaysCommitted: {
            formatByValue: (val)=>{
                if(val > 30 && val <= 60){
                    return {color: "orange", fontWeight: "bold", textAlign: "center"}
                }
                if(val > 60){
                    return {color: "red", fontWeight: "bold", textAlign: "center"}
                }
                return {color: "green", fontWeight: "bold", textAlign: "center"}
            }
        }
    }), []);

    return (
        <div className="flex-grow overflow-auto min-h-0">
            <CommonDataView
                isLoading={isLoading}
                objectsArray={filteredDataArray}
                sortData={sortData}
                sortOrder={sortOrder}
                fieldOrder={fieldOrder}
                setFieldOrder={setFieldOrder}
                parentSheetName={parentSheetName}
                filterData={filterData}
                filterObject={filterObject}
                sheetName={ownSheetName}
                selectRow={selectRow}
                selectedRow={selectedRow}
                openModalEdit={openModalEdit}
                primaryKeyName={"VehicleID"}
                formattingRules={formattingRules}
                pagination={pagination}
                setPagination={setPagination}
                searchPlaceholder="No. Plate or Chassis No."
            />
            {
                modalVisible.visible &&  filteredDataArray[currentVehicleIndex] &&
                <AddEditCommitted
                    formMode={modalVisible.mode}
                    showModal={setModalVisibility}
                    car={filteredDataArray[currentVehicleIndex]}
                    parentSheetName={parentSheetName}
                    //In add mode, read the origin that the user selected. In edit mode, read the origin from the record's origin field
                    origin={modalVisible.mode==="add" ? modalVisible.origin : filteredDataArray[currentVehicleIndex].Origin}
                    //The sheet as the user currently has it filtered and sorted.
                    //It gives the header its record stepper and its finder, so
                    //reaching the next deal no longer means closing the modal.
                    //This sheet paginates server-side, so the set is the current
                    //PAGE — the same rows the sheet itself is showing.
                    fleet={filteredDataArray}
                    fleetIndex={currentVehicleIndex}
                    onStep={setCurrentVehicle}
                />
            }
            <FloatingDownloadButton 
                data={filteredDataArray}
                fieldOrder={fieldOrder}
                sheetName={ownSheetName}
                pagination={pagination}
                filterObject={filterObject}
            />     
        </div>
    )
}

function Summary({ pagination, setPagination }) {
    const [dateFrom, setDateFrom] = useState(pagination.dateFrom ?? "");
    const [dateTo, setDateTo] = useState(pagination.dateTo ?? "");

    //Dates stay explicit-apply: unlike typing a plate, picking "From" without
    //"To" is a half-formed query and shouldn't fire a fetch on its own.
    const applyDates = () => setPagination(prev => ({
        ...prev, dateFrom, dateTo, currentPage: 1,
    }));

    const clearDates = () => {
        setDateFrom("");
        setDateTo("");
        setPagination(prev => ({ ...prev, dateFrom: "", dateTo: "", currentPage: 1 }));
    };

    return (
        <SheetFooter
            summaryId="sales-summary"
            countLabel="Vehicles Displayed:"
            searchPlaceholder="No. plate or chassis no."
            pagination={pagination}
            setPagination={setPagination}
            dateFilter={{ from: dateFrom, to: dateTo, setFrom: setDateFrom,
                          setTo: setDateTo, onApply: applyDates, onClear: clearDates }}
        />
    );
}

export default Cash;